/* eslint-disable no-nested-ternary */
// @flow
import {
    Box, Button, CheckBox, ThemeContext,
} from 'grommet';
import * as React from 'react';
import { FormattedMessage } from 'react-intl';
import BaseSelect from 'esm-web-pro/components/forms/fields/BaseSelect';
import { withRouter } from 'react-router-dom';
import { bindActionCreators } from 'redux';
import * as sidePanelActions from 'esm-web-pro/redux/side-panel/actions';
import { FieldWrapper } from 'speed-js-react-web';
import ActionButtons from 'esm-web-pro/components/forms/ActionButtons';
import Settings from 'esm-web-pro/components/tables/Settings';
import { makeGetSidePanel } from 'esm-web-pro/redux/side-panel/selectors';
import { connect } from 'react-redux';
import SidePane from '../../components/layout/SidePane';


type SidePanelActionProps = {
    openPanel: (key: string, url: ?string, skipSensitivity?: boolean) => { key: string, url: string },
    closePanel: (key: string, skipSensitivity?: boolean) => { key: string },
    closeAllPanels: (skipSensitivity?: boolean) => { },
}

type Props = {
    ...$Exact<SidePanelActionProps>,
    columns: Array<{
        Header: React.Node,
        accessor?: string,
        [string]: any,
    }>,
    initialExcludedColumns: Array<number>,
    container: React.ComponentType<{ tableId: string }>,
    onSubmit: (excludedColumns: Array<number>, rc: number) => void,
    tableId: string,
    numberOfRows: number,
    defaultExcludedColumns: Array<number>,
    defaultNumberOfRows: number,
    buttonProps?: Object,
    match: Object,
}

type State = {
    excludedColumns: Array<number>,
    rowCount: number,
}

const twentyFive = 25;
const fifty = 50;
const oneHundred = 100;
const twoHundredFifty = 250;
const fiveHundred = 500;

const numberOfRowsPerPage = {
    [twentyFive]: 25,
    [fifty]: 50,
    [oneHundred]: 100,
    [twoHundredFifty]: 250,
    [fiveHundred]: 500,
};

const COLUMN_SETTINGS_EDIT_KEY = 'COLUMN_SETTINGS_EDIT_KEY';

class ColumnSettingsFormComponent extends React.Component<Props, State> {
    COLUMN_SETTINGS_EDIT_KEY = 'COLUMN_SETTINGS_EDIT_KEY';

    FILTER_EDIT = 'FILTER_EDIT_KEY';

    columnsSidePanelTitle = (
        <FormattedMessage
            id="columns-title"
            defaultMessage="Columns"
        />
    );

    constructor(props: Props) {
        super(props);

        this.state = {
            excludedColumns: props.initialExcludedColumns,
            rowCount: props.numberOfRows,
        };
    }

    componentDidUpdate(prevProps: Props) {
        const { initialExcludedColumns } = this.props;
        if (prevProps.initialExcludedColumns !== initialExcludedColumns) {
            // eslint-disable-next-line react/no-did-update-set-state
            this.setState({ excludedColumns: initialExcludedColumns });
        }
    }

    getTableSpecificKey = () => {
        const { tableId } = this.props;
        return `${this.COLUMN_SETTINGS_EDIT_KEY}:${tableId}`;
    };

    // eslint-disable-next-line react/sort-comp
    key = this.getTableSpecificKey();

    handleSubmit = () => {
        const { onSubmit } = this.props;
        const { excludedColumns, rowCount } = this.state;
        onSubmit(excludedColumns, rowCount);
    };

    handleReset = () => {
        this.setState((prevState) => ({
            ...prevState,
            excludedColumns: [],
        }));
    }

    loadDefault = () => {
        const { defaultExcludedColumns, defaultNumberOfRows } = this.props;
        this.setState((prevState) => ({
            ...prevState,
            excludedColumns: defaultExcludedColumns,
            rowCount: defaultNumberOfRows,
        }));
    }

    loadCurrent = () => {
        const { initialExcludedColumns, numberOfRows } = this.props;

        this.setState((prevState) => ({
            ...prevState,
            excludedColumns: initialExcludedColumns,
            rowCount: numberOfRows,
        }));
    }

    handleChange = (columnidx: number) => {
        this.setState((prevState) => {
            if (prevState.excludedColumns.includes(columnidx)) {
                return {
                    ...prevState,
                    excludedColumns:
                        prevState.excludedColumns.filter((idx) => idx !== columnidx),
                };
            }

            return {
                ...prevState,
                excludedColumns: prevState.excludedColumns.concat([columnidx]),
            };
        });
    };

    handleRowCountChange = (rc: number) => {
        this.setState({ rowCount: rc });
    }

    compareColumnValues = () => {
        const {
            initialExcludedColumns, defaultExcludedColumns, defaultNumberOfRows, numberOfRows,
        } = this.props;
        const { excludedColumns, rowCount } = this.state;

        const initialExcludedColumnsSorted = initialExcludedColumns.sort((a, b) => a - b);
        const excludedColumnsSorted = excludedColumns.sort((a, b) => a - b);

        const compareCurrentAndDefault = excludedColumnsSorted.join('') === defaultExcludedColumns.join('')
            && Number(rowCount) === Number(defaultNumberOfRows);
        const compareCurrentAndInitial = excludedColumnsSorted.join('') === initialExcludedColumnsSorted.join('')
            && Number(rowCount) === Number(numberOfRows);

        return [excludedColumnsSorted.length === 0, compareCurrentAndDefault, compareCurrentAndInitial];
    }

    createFormContextObject = () => {
        const {
            initialExcludedColumns, columns,
        } = this.props;
        const { excludedColumns } = this.state;
        const formContextObject = {};

        for (let i = 0; i < columns.length; i += 1) {
            if (excludedColumns.includes(i) && initialExcludedColumns.includes(i)) {
                formContextObject[i] = { border: 'rgba(0, 0, 0, 0.33)', apply: false };
            } else if (!excludedColumns.includes(i) && !initialExcludedColumns.includes(i)) {
                formContextObject[i] = { border: '#0074d9', apply: false };
            } else if ((excludedColumns.includes(i) && !initialExcludedColumns.includes(i))
                || (!excludedColumns.includes(i) && initialExcludedColumns.includes(i))) {
                formContextObject[i] = { border: 'rgba(253,208,137,1)', apply: true };
            }
        }
        return formContextObject;
    }

    actions = () => {
        const { rowCount } = this.state;

        return (
            <ActionButtons
                onClose={() => {
                    this.handleReset();
                }}
                clearButtonLabel={<FormattedMessage id="settings-btn-reset" defaultMessage="All" />}
                onDefault={() => {
                    this.loadDefault();
                }}
                defaultButtonLabel={<FormattedMessage id="settings-btn-default" defaultMessage="Default" />}
                onSave={() => {
                    this.handleSubmit();
                }}
                filterButtonLabel={<FormattedMessage id="settings-btn-filter" defaultMessage="Save" />}
                disabled={!rowCount}
                disabledReset={this.compareColumnValues()[0]}
                disabledDefault={this.compareColumnValues()[1]}
                disabledSubmit={this.compareColumnValues()[2]}
            />
        );
    }

    handleClose = () => {
        const { closePanel } = this.props;
        closePanel(this.key, true);
    };

    togglePanel = () => {
        const {
            closePanel, closeAllPanels, openPanel, match, isPanelOpened,
        } = this.props;

        if (isPanelOpened) {
            closePanel(this.key, true);
            this.loadCurrent();
        } else {
            closeAllPanels();
            closePanel(this.FILTER_EDIT, true);
            openPanel(this.key, this.columnsSidePanelTitle, match?.url, false);
        }
    };

    renderSettings = () => {
        const { columns, numberOfRows } = this.props;
        const { excludedColumns, rowCount } = this.state;
        const styles = this.createFormContextObject();

        return (
            <Box
                // ref={formRef}
                as="form"
                onSubmit={(e) => {
                    e.preventDefault();
                    this.handleSubmit();
                }}
                onReset={this.handleReset}
                flex
            >
                <Box gap="small" justify="start" alignContent="start" overflow="auto" pad="small" flex>
                    <FieldWrapper
                        key="row-count"
                        required
                        label={(
                            <FormattedMessage id="row-count" defaultMessage="Row count" />
                        )}
                    >
                        <BaseSelect
                            allowEmptyChoice={false}
                            choices={numberOfRowsPerPage}
                            onChange={this.handleRowCountChange}
                            value={rowCount}
                            customStyle={Number(rowCount) !== Number(numberOfRows)
                                ? { border: '3px solid rgba(253,208,137,1)' }
                                : { border: '1px solid rgba(0, 0, 0, 0.33)' }}
                        />
                    </FieldWrapper>
                    <FieldWrapper
                        key="column-choice"
                        label={(
                            <FormattedMessage id="column-choice" defaultMessage="Column choice" />
                        )}
                    />
                    {columns.map((column, index) => (
                        <ThemeContext.Extend
                            key={index.toString()}
                            value={{
                                checkBox: {
                                    border: {
                                        color: {
                                            dark: styles[index].border, light: styles[index].border,
                                        },
                                        width: '2px',
                                    },
                                    check: {
                                        radius: '4px',
                                        thickness: '4px',
                                        extend: { borderColor: styles[index].border, borderWidth: '3px' },
                                    },
                                },
                            }}
                        >
                            <CheckBox
                                key={index.toString()}
                                checked={!excludedColumns.includes(index)}
                                label={column.Header
                                    ? column.Header
                                    : <FormattedMessage id="projmanag-task-actions" defaultMessage="Actions" />}
                                onChange={() => this.handleChange(index)}
                            />
                        </ThemeContext.Extend>
                    ))}
                </Box>
            </Box>
        );
    }

    render() {
        const {
            buttonProps, isPanelOpened,
        } = this.props;

        return (
            <React.Fragment>
                <Button
                    onClick={this.togglePanel}
                    hoverIndicator="light-1"
                    pad={{ vertical: '10px', horizontal: 'small' }}
                    style={{ backgroundColor: isPanelOpened ? '#F8F8F8' : '' }}
                    {...buttonProps}
                >
                    <Box pad={{ vertical: '10px', horizontal: 'small' }}>
                        <Settings />
                    </Box>
                </Button>
                {isPanelOpened && (
                    <SidePane
                        id={this.key}
                        title={this.columnsSidePanelTitle}
                        onClose={this.handleClose}
                        actions={this.actions()}
                        style={{
                            borderRight: '1px solid #3a3a3a',
                            borderRadius: '0px',
                            width: '300px',
                        }}
                    >
                        <Box pad={{ horizontal: 'small' }} height="100%" style={{ fontSize: '12px' }}>
                            {this.renderSettings()}
                        </Box>
                    </SidePane>
                )}
            </React.Fragment>
        );
    }
}

const mapStateToProps = (state, props) => ({
    isPanelOpened: !!makeGetSidePanel(`${COLUMN_SETTINGS_EDIT_KEY}:${props.tableId}`)(state),
});

const mapDispatchToProps = (dispatch) => (
    bindActionCreators(sidePanelActions, dispatch)
);

ColumnSettingsFormComponent.defaultProps = {
    buttonProps: {},
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(ColumnSettingsFormComponent));
